// src/components/appointment/AppointmentPrintService.js
import React, { useRef } from 'react';

/**
 * Function to handle appointment printing
 * @param {Object} appointment - The appointment to print
 * @returns {Object} - The print functions and ref
 */
export const usePrintAppointment = () => {
  const printFrameRef = useRef(null);
  
  // Function to generate print iframe HTML
  const generatePrintContent = (appointment) => {
    // Format the date for display
    const appointmentDate = appointment.createdAt ? 
      new Date(appointment.createdAt).toLocaleDateString("en-US", { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }) : 'N/A';

    return `
      <html>
        <head>
          <title>Appointment Slip - ${appointment.patientName}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.4; /* Reduced line height for compactness */
              color: #333;
              max-width: 700px; /* Slightly reduced max-width */
              margin: 0 auto;
              padding: 15px; /* Reduced padding */
              font-size: 13px; /* Slightly smaller base font size */
            }
            .header {
              text-align: center;
              margin-bottom: 15px; /* Reduced margin */
              padding-bottom: 15px; /* Reduced padding */
              border-bottom: 1px solid #ccc;
            }
            .header h1 {
                font-size: 20px; /* Smaller header title */
                margin-bottom: 5px;
            }
            .header h2 {
                font-size: 16px; /* Smaller sub-header */
                margin-top: 10px;
                margin-bottom: 5px;
            }
            .header p {
                margin: 2px 0; /* Reduced paragraph spacing */
            }
            .section {
              margin-bottom: 15px; /* Reduced margin */
            }
            .grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 15px; /* Reduced gap */
            }
            .label {
              font-weight: bold;
              margin-right: 3px; /* Slightly reduced margin */
            }
            .footer {
              margin-top: 30px; /* Reduced margin */
              text-align: center;
              font-size: 11px; /* Smaller footer font size */
              color: #777;
            }
            .note {
              border-top: 1px solid #ccc;
              padding-top: 15px; /* Reduced padding */
              font-size: 12px; /* Smaller note font size */
              color: #666;
            }
            .reason {
              padding: 8px; /* Reduced padding */
              border: 1px solid #ccc;
              border-radius: 4px;
            }
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Mukti Hospital</h1>
            <p>Race Course, Comilla, Bangladesh</p>
            <p>Phone: 01834220141 | Email: muktihospitalpvtlimited@gmail.com</p>
            <h2>Appointment Confirmation</h2>
            <p>Date: ${appointmentDate} | Appointment ID: ${appointment.id}</p>
            <h2>Your Serial Number is: ${appointment.serialNumber}</h2>
          </div>
          
          <div class="grid">
            <div class="section">
              <h3>Patient Information</h3>
              <p><span class="label">Name:</span> ${appointment.patientName}</p>
              <p><span class="label">Phone:</span> ${appointment.mobileNumber}</p>
              ${appointment.age ? `<p><span class="label">Age:</span> ${appointment.age} years</p>` : ''}
              ${appointment.weight ? `<p><span class="label">Weight:</span> ${appointment.weight} kg</p>` : ''}
              ${appointment.bloodGroup ? `<p><span class="label">Blood Group:</span> ${appointment.bloodGroup.replace('_POSITIVE', '+').replace('_NEGATIVE', '-')}</p>` : ''}
              ${appointment.address ? `<p><span class="label">Address:</span> ${appointment.address}</p>` : ''}
            </div>
            
            <div class="section">
              <h3>Appointment Details</h3>
              <p><span class="label">Doctor:</span> ${appointment.doctorName}</p>
              <p><span class="label">Serial Number:</span> ${appointment.serialNumber || 'Pending'}</p>
              ${appointment.consultationFee ? `<p><span class="label">Fee:</span> ${appointment.consultationFee} Tk</p>` : ''}
              ${appointment.paymentMethod ? `<p><span class="label">Paid via:</span> ${appointment.paymentMethod}</p>` : ''}
            </div>
          </div>
          
          ${appointment.reason ? `
          <div class="section">
            <h3>Appointment Reason</h3>
            <p class="reason">${appointment.reason}</p>
          </div>
          ` : ''}
          
          <div class="note">
            <p>Please arrive 15 minutes before your appointment time with this slip.</p>
            <p>For any changes or cancellations, please contact us 24 hours in advance.</p>
          </div>
          
          <div class="footer">
            <p>This is a computer-generated document. No signature is required.</p>
          </div>
        </body>
      </html>
    `;
  };
  
  // Function to handle print process
  const handlePrint = (appointment) => {
    if (!appointment) return;
    
    setTimeout(() => {
      if (printFrameRef.current) {
        const iframe = printFrameRef.current;
        const iframeWindow = iframe.contentWindow || iframe;
        
        try {
          // Set the content of the iframe before printing
          iframeWindow.document.open();
          iframeWindow.document.write(generatePrintContent(appointment));
          iframeWindow.document.close();
          
          iframeWindow.focus();
          iframeWindow.print();
        } catch (error) {
          console.error("Printing failed:", error);
          alert("Printing failed. Please try again.");
        }
      }
    }, 500);
  };
  
  // Render hidden iframe component
  const PrintFrame = ({ appointment }) => {
    // We will dynamically set srcDoc inside handlePrint to ensure it's fresh
    // So, we don't set srcDoc here initially.
    return (
      <iframe
        ref={printFrameRef}
        style={{
          height: '0',
          width: '0',
          position: 'absolute',
          display: 'none',
        }}
        title="Print Frame"
      />
    );
  };
  
  return {
    handlePrint,
    PrintFrame
  };
};

export default usePrintAppointment;